import React, { useEffect, useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { updateStudentScholarshipStatusOperation } from '../operations/update-student-scholarship-status-operation';
import {
  StudentScholarshipsEntity,
  StudentScholarshipsStatus,
} from '../../../../../../api/types/students/student-scholarships/student-scholarship-entity';

interface UpdateStudentScholarshipStatusDialogProps {
  visible: boolean;
  userId: number;
  scholarship: StudentScholarshipsEntity | null;
  onHide: () => void;
}

interface StatusOption {
  label: string;
  value: StudentScholarshipsStatus;
}

export const UpdateStudentScholarshipStatusDialog: React.FC<
  UpdateStudentScholarshipStatusDialogProps
> = ({ visible, userId, scholarship, onHide }) => {
  const [selectedStatus, setSelectedStatus] =
    useState<StudentScholarshipsStatus | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible && scholarship) {
      setSelectedStatus(scholarship.studentScholarshipsStatus);
    }
  }, [visible, scholarship]);

  const getStatusText = (status: StudentScholarshipsStatus) => {
    switch (status) {
      case StudentScholarshipsStatus.PENDING:
        return 'ממתינה';
      case StudentScholarshipsStatus.APPROVED:
        return 'מאושרת';
      case StudentScholarshipsStatus.FAILED:
        return 'נכשלה';
      case StudentScholarshipsStatus.CANCELLED:
        return 'בוטלה';
      default:
        return status;
    }
  };

  const statusOptions: StatusOption[] = useMemo(
    () =>
      (
        Object.values(StudentScholarshipsStatus) as StudentScholarshipsStatus[]
      ).map((value) => ({
        label: getStatusText(value),
        value,
      })),
    [],
  );

  const handleClose = () => {
    if (loading) return;
    onHide();
  };

  const handleApprove = async () => {
    if (!scholarship || !selectedStatus) {
      return;
    }

    await updateStudentScholarshipStatusOperation(
      userId,
      scholarship.id,
      selectedStatus,
      setLoading,
      onHide,
    );
  };

  const isValid = !!selectedStatus && !!scholarship;

  return (
    <Dialog
      header="עדכון סטטוס מלגה"
      visible={visible}
      onHide={handleClose}
      modal
      style={{ width: '420px', direction: 'rtl' }}
      closable={!loading}
      footer={
        <div
          style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}
        >
          <Button
            label="ביטול"
            icon="pi pi-times"
            severity="secondary"
            text
            onClick={handleClose}
            disabled={loading}
          />
          <Button
            label={loading ? 'מעדכן...' : 'אישור'}
            icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
            onClick={handleApprove}
            disabled={!isValid || loading}
            loading={loading}
          />
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div className="p-field">
          <label
            htmlFor="status"
            style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: 600,
              color: '#374151',
            }}
          >
            סטטוס מלגה *
          </label>
          <Dropdown
            id="status"
            value={selectedStatus}
            options={statusOptions}
            optionLabel="label"
            optionValue="value"
            onChange={(e) => setSelectedStatus(e.value)}
            style={{ width: '100%' }}
            placeholder="בחר סטטוס"
            disabled={loading || !scholarship}
          />
        </div>
      </div>
    </Dialog>
  );
};
